import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Role, EmploymentType } from '@/types';
import { Tables, TablesInsert, TablesUpdate } from '@/integrations/supabase/types';
import { toast } from '@/hooks/ui/use-toast';

type RoleRow = Tables<'roles'>;
type RoleInsert = TablesInsert<'roles'>;
type RoleUpdate = TablesUpdate<'roles'>;

// Extended row type with joined employment_types
type RoleRowWithRelations = RoleRow & {
  employment_types?: EmploymentType | null;
  employment_type_id?: string | null;
};

// Convert database row to Role interface
const mapRoleRowToRole = (row: RoleRowWithRelations): Role => ({
  id: row.id,
  business_id: row.business_id,
  name: row.name,
  description: row.description,
  default_pay_rate: row.default_pay_rate,
  pay_type: row.pay_type,
  hours_per_month: row.hours_per_month,
  employment_type_id: row.employment_type_id || null,
  employmentType: row.employment_types || null,
  dotypos_access_level: (row as any).dotypos_access_level ?? null,
  created_at: row.created_at || new Date().toISOString(),
  updated_at: row.updated_at || new Date().toISOString(),
});

// Fetch roles for a business with direct state management
export const useRoles = (businessId: string) => {
  const [data, setData] = useState<Role[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchRoles = useCallback(async () => {
    if (!businessId) {
      setData([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data: rolesData, error: fetchError } = await supabase
        .from('roles')
        .select('*')
        .eq('business_id', businessId)
        .order('name', { ascending: true });

      if (fetchError) {
        console.error('Error fetching roles:', fetchError);
        throw fetchError;
      }

      setData(rolesData?.map(mapRoleRowToRole) || []);
    } catch (err) {
      console.error('Error fetching roles:', err);
      setError(err as Error);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchRoles
  };
};

// Create a new role with direct database operations
export const useCreateRole = () => {
  const [isLoading, setIsLoading] = useState(false);

  const mutateAsync = useCallback(async (
    roleData: Omit<RoleInsert, 'id' | 'created_at' | 'updated_at'>,
    onSuccess?: (role: Role) => void
  ) => {
    setIsLoading(true);

    try {
      const { data, error } = await supabase
        .from('roles')
        .insert([roleData])
        .select()
        .single();

      if (error) {
        console.error('Error creating role:', error);
        throw error;
      }

      const newRole = mapRoleRowToRole(data);

      toast.success("Role created successfully", {
        description: `Role "${newRole.name}" has been created.`,
      });

      // Call success callback for immediate UI updates
      onSuccess?.(newRole);

      return newRole;
    } catch (error) {
      console.error('Error creating role:', error);
      toast.error("Error creating role", {
        description: error instanceof Error ? error.message : String(error),
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { mutateAsync, isLoading };
};

// Update an existing role with direct database operations
export const useUpdateRole = () => {
  const [isLoading, setIsLoading] = useState(false);

  const mutateAsync = useCallback(async (
    { id, updates }: { id: string; updates: RoleUpdate },
    onSuccess?: (role: Role) => void
  ) => {
    setIsLoading(true);

    try {
      const { data, error } = await supabase
        .from('roles')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating role:', error);
        throw error;
      }

      const updatedRole = mapRoleRowToRole(data);

      toast.success("Role updated successfully", {
        description: `Role "${updatedRole.name}" has been updated.`,
      });

      // Call success callback for immediate UI updates
      onSuccess?.(updatedRole);

      return updatedRole;
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error("Error updating role", {
        description: error instanceof Error ? error.message : String(error),
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { mutateAsync, isLoading };
};

// Delete a role with direct database operations
export const useDeleteRole = () => {
  const [isLoading, setIsLoading] = useState(false);

  const mutateAsync = useCallback(async (
    { id, businessId }: { id: string; businessId: string },
    onSuccess?: () => void
  ) => {
    setIsLoading(true);

    try {
      const { error } = await supabase
        .from('roles')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting role:', error);
        throw error;
      }

      toast.success("Role deleted successfully", {
        description: "The role has been deleted.",
      });

      // Call success callback for immediate UI updates
      onSuccess?.();

      return { id, businessId };
    } catch (error) {
      console.error('Error deleting role:', error);
      toast.error("Error deleting role", {
        description: error instanceof Error ? error.message : String(error),
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { mutateAsync, isLoading };
};

// Get a specific role by ID with direct state management
export const useRole = (roleId: string) => {
  const [data, setData] = useState<Role | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchRole = useCallback(async () => {
    if (!roleId) {
      setData(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data: roleData, error: fetchError } = await supabase
        .from('roles')
        .select('*')
        .eq('id', roleId)
        .single();

      if (fetchError) {
        console.error('Error fetching role:', fetchError);
        throw fetchError;
      }

      setData(mapRoleRowToRole(roleData));
    } catch (err) {
      console.error('Error fetching role:', err);
      setError(err as Error);
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [roleId]);

  useEffect(() => {
    fetchRole();
  }, [fetchRole]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchRole
  };
};

// Get roles with employee count using direct state management
export const useRolesWithEmployeeCount = (businessId: string) => {
  const [data, setData] = useState<(Role & { employee_count: number })[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchRolesWithEmployeeCount = useCallback(async () => {
    if (!businessId) {
      setData([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data: rolesData, error: fetchError } = await supabase
        .from('roles')
        .select(`
          *,
          employees:employees(count),
          employment_types(id, code, label_native, label_en, category)
        `)
        .eq('business_id', businessId)
        .order('name', { ascending: true });

      if (fetchError) {
        console.error('Error fetching roles with employee count:', fetchError);
        throw fetchError;
      }

      const processedData = rolesData?.map(role => ({
        ...mapRoleRowToRole(role),
        employee_count: role.employees?.[0]?.count || 0
      })) || [];

      setData(processedData);
    } catch (err) {
      console.error('Error fetching roles with employee count:', err);
      setError(err as Error);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    fetchRolesWithEmployeeCount();
  }, [fetchRolesWithEmployeeCount]);

  return {
    data,
    isLoading,
    error,
    refetch: fetchRolesWithEmployeeCount
  };
};